import { formatPriceWithCommas, isoToLocalDateTime } from '../../utils/stringUtils';

import {
    Box,
    Paper,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Chip,
} from '@mui/material';


export default function InvoiceTemplate({
    title = "INVOICE",
    invoice,
    paperSize = 'A4',
    invoiceItems = [],
    showStatus = true,
    showPrintedDate = false,
}) {

    const paperSizeWidthMap = {
        'A4': '210mm',
        'A5': '148mm',
        'Letter': '8.5in',
        'Legal': '8.5in',
    };

    // A4 paper dimensions: 210mm x 297mm
    const PAPER_WIDTH = paperSizeWidthMap[paperSize] || '210mm'; // defaults to A4 width

    // Status chip helper
    const getStatusChip = (status) => {
        const labelMap = {
            'ISSUED': 'Issued',
            'VOID': 'Void',
            'UNCOLLECTABLE': 'Uncollectable'
        };
        const colorMap = {
            'ISSUED': 'success',
            'VOID': 'default',
            'UNCOLLECTABLE': 'error'
        };
        return (
            <Chip
                label={labelMap[status] || 'Unknown'}
                color={colorMap[status] || 'default'}
                size="small"
                variant="outlined"
                sx={{ minWidth: '80px' }}
            />
        );
    };

    // Payment status chip helper
    const getPaymentChip = (status) => {
        const labelMap = {
            'N/A': 'Not applicable',
            'UNPAID': 'Not paid',
            'PARTIALLY_PAID': 'Partially paid',
            'FULLY_PAID': 'Fully Paid'
        };
        const colorMap = {
            'N/A': 'default',
            'UNPAID': 'warning',
            'PARTIALLY_PAID': 'primary',
            'FULLY_PAID': 'success'
        };
        return (
            <Chip
                label={labelMap[status] || 'Unknown'}
                color={colorMap[status] || 'default'}
                size="small"
                variant="outlined"
                sx={{ minWidth: '100px' }}
            />
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                '@media print': {
                    display: 'block',
                },
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: PAPER_WIDTH,
                    // minHeight: A4_MIN_HEIGHT,
                    p: 4,
                    backgroundColor: '#fff',
                    boxSizing: 'border-box',
                    '@media print': {
                        boxShadow: 'none',
                        width: '100%',
                        minHeight: 'auto',
                        p: 0,
                    },
                }}
            >
                {/* Invoice Header */}
                <Box sx={{ mb: 4 }}>
                    <Grid container justifyContent="space-between" alignItems="flex-start">
                        <Grid item xs={6}>
                            {/* Company/Store Info */}
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                {title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                {invoice.location?.address || ''}
                            </Typography>
                        </Grid>

                        <Grid item xs={6} textAlign="right">
                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                INVOICE
                            </Typography>
                            {/* Invoice Status */}
                            {showStatus && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    {getStatusChip(invoice.status)}
                                    {getPaymentChip(invoice.paymentStatus)}
                                </Box>
                            )}
                        </Grid>

                    </Grid>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Invoice Details Section */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            INVOICE DETAILS
                        </Typography>
                        <Box sx={{ '& > *': { mb: 0.5 } }}>
                            <Typography variant="body2">
                                <strong>Invoice No:</strong> {invoice.invoiceNumber || 'N/A'}
                            </Typography>
                            {/* <Typography variant="body2">
                                <strong>Origin:</strong> {invoice.originType || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Currency:</strong> {invoice.currency || 'N/A'}
                            </Typography> */}
                            <Typography variant="body2">
                                <strong>Invoice Date:</strong> {isoToLocalDateTime(invoice.confirmedAt || invoice.createdAt, { date: true, time: false })}
                            </Typography>
                            {showPrintedDate && (
                                <Typography variant="body2">
                                    <strong>Printed Date:</strong> {isoToLocalDateTime(new Date(), { date: true, time: false })}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            INVOICE TO:
                        </Typography>
                        <Box sx={{ '& > *': { mb: 0.5 } }}>
                            {invoice.customerData ? (
                                <>
                                    <Typography variant="body2">
                                        {invoice.customerData?.name || 'Name: Not available'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                        {invoice.customerData?.address || 'Address: Not available'}
                                    </Typography>
                                    <Typography variant="body2" mt={2}>
                                        Phone: {invoice.customerData?.phoneNumber || 'Not available'}
                                    </Typography>
                                    <Typography variant="body2">
                                        Email: {invoice.customerData?.email || 'Not available'}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Walk-in Customer
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    {invoice.vehicleData && (
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                VEHICLE:
                            </Typography>
                            <Box sx={{ '& > *': { mb: 0.5 } }}>
                                <Typography variant="body2">
                                    Reg No: {invoice.vehicleData?.registrationNumber || 'Not available'}
                                </Typography>
                                <Typography variant="body2">
                                    {invoice.vehicleData?.make || ''} {invoice.vehicleData?.model || ''} {invoice.vehicleData?.year || ''}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {/* Location Information */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        LOCATION
                    </Typography>
                    <Typography variant="body2">
                        <strong>Store:</strong> {invoice.location?.name || 'N/A'}
                    </Typography>
                </Box>

                {/* Items Table */}
                <TableContainer sx={{ mb: 4 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Discount</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Line Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoiceItems && invoiceItems.length > 0 ? (
                                invoiceItems.map((item, index) => (
                                    <TableRow key={item.id || index}>

                                        {/* Line number */}
                                        <TableCell>
                                            {index + 1}
                                        </TableCell>

                                        {/* Item name */}
                                        <TableCell>
                                            {item.itemData?.name || 'N/A'}
                                        </TableCell>

                                        {/* Quantity */}
                                        <TableCell align="right">
                                            {item.quantity || 0} {item.itemData.uom || ''}
                                        </TableCell>

                                        {/* Unit Price */}
                                        <TableCell align="right">
                                            {formatPriceWithCommas(String(item.unitPrice || 0))} / {item.itemData.uom || ''}
                                        </TableCell>

                                        {/* Discount */}
                                        <TableCell align="right">
                                            {item.totalDiscount > 0 && (
                                                <Typography variant='body2' color="green">
                                                    - {formatPriceWithCommas(String(item.totalDiscount))}
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Line Total */}
                                        <TableCell align="right">
                                            <Typography variant='body2' fontWeight="bold">
                                                {formatPriceWithCommas(String(item.netTotal || 0))}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No items in this invoice
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Totals Section */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
                    <Box sx={{ width: '400px' }}>
                        <Grid container spacing={1} mt={4}>
                            <Grid item xs={6}>
                                <Typography variant="body2">Subtotal (before discounts):</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                                <Typography variant="body2">
                                    {invoice.currency} {formatPriceWithCommas(invoice.subTotal || '0')}
                                </Typography>
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="body2">Total Discounts:</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">

                                {/* Show negative sign for discount if discountAmount is greater than 0 */}
                                {invoice.discountAmount > 0 ? (
                                    <Typography variant="body2" color="green">
                                        - {invoice.currency} {formatPriceWithCommas(invoice.discountAmount || '0')}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2">
                                        {invoice.currency} {formatPriceWithCommas(invoice.discountAmount || '0')}
                                    </Typography>
                                )}
                            </Grid>

                            {/* <Grid item xs={6}>
                                <Typography variant="body2">Tax:</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                                <Typography variant="body2">
                                    {invoice.currency} {formatPriceWithCommas(invoice.taxAmount || '0')}
                                </Typography>
                            </Grid> */}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="h6" fontWeight="bold">
                                    Net Total:
                                </Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                                <Typography variant="h6" fontWeight="bold">
                                    {invoice.currency} {formatPriceWithCommas(invoice.netTotal || '0')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>

                {/* Notes Section */}
                {invoice.notes && (
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            NOTES
                        </Typography>
                        <Typography variant="body2">
                            {invoice.notes}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ mb: 3 }} />

                {/* Footer */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Thank you for choosing our service!
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}